import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import * as platform from '@platform';
import { useNavigate } from 'react-router-dom';
import { useSession } from './session.js';
import i18n from '../i18n.js';
import type { Notification } from '@worki/shared';
import { api, apiHeaders, apiUrl } from './api.js';

/** First wait after a drop. Matches the server's `retry:` hint. */
const RETRY_MIN_MS = 3_000;
/** Ceiling. A tab left open against a server that is down settles here. */
const RETRY_MAX_MS = 60_000;

/** Everything the stream's events touch, for the catch-up after a gap. */
const ALL_KEYS = [
  ['notifications'],
  ['dashboard'],
  ['quotes'],
  ['quote'],
  ['activity'],
  ['reminders'],
  ['agents'],
  ['agent'],
  ['voice-job'],
];

/**
 * INFRA-009 / DASH-001 — subscribes to the server event stream and invalidates
 * the affected caches, so badges and lists update without a refresh.
 *
 * Fetch-based (`fetchEventSource`) rather than `EventSource`, so an
 * `Authorization` header can ride the request. A query-string token is
 * rejected: the API logs `req.originalUrl` in production.
 *
 * CapacitorHttp must stay disabled — it monkey-patches `window.fetch` and
 * breaks this stream (and the FormData scan upload).
 *
 * The reconnection is ours rather than the transport's. The library would
 * retry on a fixed interval for as long as the tab is open. A server that is
 * down therefore gets twenty requests a minute from every tab, forever; a
 * phone pays for that in radio wake-ups. So the stream is closed on error and
 * reopened on a delay that doubles to a minute, with jitter so that a server
 * coming back up is not hit by every tab in the same instant.
 *
 * A hidden tab holds no stream at all: it cannot show a badge, and the socket
 * costs the server a connection either way. It reconnects when the tab is
 * looked at again, and immediately when the network comes back rather than
 * waiting out a backoff that is measuring the wrong thing.
 */
export function useRealtime(enabled: boolean): void {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { session } = useSession();
  const businessId = session?.business.id;
  const userId = session?.user.id;

  useEffect(() => {
    if (!enabled || !businessId || !userId || !platform.supportsNativeNotifications) return;
    return platform.startNotificationSession({ businessId, userId }, {
      register: (token, signal) => api.put<void>('/notifications/devices', { token }, { signal }),
      remove: (token, signal) => api.post<void>('/notifications/devices/remove', { token }, { signal }),
      resolve: async id => {
        const notice = await api.get<Notification & { vars?: Record<string, unknown> }>(`/notifications/${encodeURIComponent(id)}`);
        return { ...notice, title: String(i18n.t(notice.title, notice.vars ?? {})) };
      },
      navigate: (link, id) => {
        void api.post(`/notifications/${encodeURIComponent(id)}/read`).catch(() => undefined);
        navigate(link);
      },
    });
  }, [enabled, businessId, userId, navigate]);

  useEffect(() => {
    if (!enabled) return undefined;

    let abort: AbortController | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let attempt = 0;
    let missedEvents = false;
    let stopped = false;

    const invalidate = (keys: string[][]) => {
      for (const key of keys) void queryClient.invalidateQueries({ queryKey: key });
    };

    const close = () => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      abort?.abort();
      abort = null;
    };

    const schedule = () => {
      if (stopped || timer) return;
      const ceiling = Math.min(RETRY_MIN_MS * 2 ** attempt, RETRY_MAX_MS);
      attempt += 1;
      // Half the ceiling plus a random half: the wait still grows, but two tabs
      // that dropped together do not come back together.
      timer = setTimeout(
        () => {
          timer = null;
          void connect();
        },
        ceiling / 2 + Math.random() * (ceiling / 2),
      );
    };

    async function connect(): Promise<void> {
      // Nothing to show and nobody watching. `visibilitychange` reopens it.
      if (stopped || document.visibilityState === 'hidden') return;
      close();

      const ctrl = new AbortController();
      abort = ctrl;

      try {
        const headers = await apiHeaders();
        if (stopped || abort !== ctrl) return;

        await fetchEventSource(apiUrl('/notifications/stream'), {
          signal: ctrl.signal,
          credentials: 'include',
          openWhenHidden: false,
          headers: Object.fromEntries(headers.entries()),
          async onopen(response) {
            if (!response.ok) throw new Error(String(response.status));
            attempt = 0;
            // Whatever happened during the gap arrived at a stream nobody was
            // holding. `refetchOnWindowFocus` is off, so without this the screen
            // keeps showing what it had before the drop until something else asks.
            if (missedEvents) {
              missedEvents = false;
              invalidate(ALL_KEYS);
            }
          },
          onmessage(ev) {
            switch (ev.event) {
              case 'notification':
                try {
                  const data = JSON.parse(ev.data) as { id?: unknown };
                  if (typeof data.id === 'string') void platform.presentNativeNotification(data.id);
                } catch { /* Malformed events cannot interrupt cache updates. */ }
                invalidate([['notifications'], ['dashboard']]);
                break;
              case 'quote.updated':
                invalidate([['quotes'], ['quote'], ['dashboard'], ['activity']]);
                break;
              case 'reminders.changed':
                invalidate([['reminders'], ['dashboard']]);
                break;
              case 'agents.changed':
                invalidate([['agents']]);
                break;
              case 'agent_run':
                invalidate([['agent']]);
                break;
              case 'voice_job':
                invalidate([['voice-job']]);
                break;
              default:
                break;
            }
          },
          onerror(err) {
            // Throwing takes retry away from the transport: left to it, it
            // would reconnect on its own fixed interval alongside ours.
            throw err;
          },
        });
      } catch {
        if (stopped || abort !== ctrl) return;
        missedEvents = true;
        abort = null;
        schedule();
      }
    }

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        platform.refreshNativeNotifications();
        // Coming back is not a failure, so it does not inherit the backoff.
        attempt = 0;
        void connect();
      } else {
        close();
      }
    };

    const onOnline = () => {
      platform.refreshNativeNotifications();
      attempt = 0;
      void connect();
    };

    void connect();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('online', onOnline);
    const offNetwork = platform.onNetworkOnline(onOnline);

    return () => {
      stopped = true;
      close();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', onOnline);
      offNetwork();
    };
  }, [enabled, queryClient]);
}
